import { Link } from "react-router-dom";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

export default function TermsOfService() {
  const seoData = {
    title: "Terms of Service | Paradise Borrowed",
    description: "Read the Terms of Service for Paradise Borrowed. Learn about eligibility, account registration, platform rules, lender and borrower terms, payments, fees, and dispute resolution.",
    url: "https://www.paradiseborrowed.com/terms-of-service"
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.url}
        type="website"
        schema={generateWebPageSchema(seoData)}
      />
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="terms-page">
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-2">Terms of Service</h1>
      <p className="text-stone-600 text-lg mb-8">
        Paradise Borrowed, Inc. — Effective Date: March 15, 2026
      </p>
      
      <div className="prose prose-stone max-w-none space-y-8">
        <p className="text-stone-600 leading-relaxed font-medium">
          Please read these Terms of Service carefully before accessing or using the Paradise Borrowed platform. By creating an account or using any feature of the platform, you agree to be bound by these Terms. If you do not agree, you may not use the platform.
        </p>

        {/* Section 1 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">1. About Paradise Borrowed</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            Paradise Borrowed, Inc. ("Paradise Borrowed," "we," "us," or "the Platform") is a Delaware corporation operating a peer-to-peer item lending and borrowing marketplace. The Platform connects independent users who wish to lend items ("Lenders") with users who wish to borrow them ("Borrowers"). Collectively, Lenders and Borrowers are referred to as "Users."
          </p>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed is a neutral marketplace platform and is not a party to any borrowing agreement between Users. We do not own, possess, inspect, or control any items listed on the Platform. We are not an insurance company, a logistics provider, or a financial institution.
          </p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">2. Eligibility</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            To register for and use the Platform, you must:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Be at least 18 years of age</li>
            <li>Be a natural person (not a corporation or automated system) registering in your own name</li>
            <li>Reside in or be visiting a supported geographic region (currently: select states within the United States and select Caribbean territories, as listed on our website)</li>
            <li>Have the legal capacity to enter into binding contracts under applicable law</li>
            <li>Not have a previously terminated Paradise Borrowed account</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mt-3">
            By registering, you represent and warrant that all of the above conditions are met and that all information you provide is accurate, current, and complete.
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">3. Account Registration & Verification</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.1 Account Creation</h3>
          <p className="text-stone-600 leading-relaxed">
            You must create an account to access the Platform's core features. You agree to provide accurate, complete, and current information and to update it promptly if it changes. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.2 Email Verification</h3>
          <p className="text-stone-600 leading-relaxed">
            All users must verify their email address before creating listings, booking items, or sending messages on Paradise Borrowed. Verification is completed by clicking the link sent to your registered email address. Email verification helps ensure account authenticity but does not constitute an endorsement or guarantee of any User's conduct or reliability.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.3 Account Security</h3>
          <p className="text-stone-600 leading-relaxed">
            You agree to notify us immediately at <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a> of any unauthorized use of your account. Paradise Borrowed is not liable for losses resulting from unauthorized account access caused by your failure to secure your credentials.
          </p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">4. Platform Use & Acceptable Conduct</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">4.1 Permitted Use</h3>
          <p className="text-stone-600 leading-relaxed">
            You may use the Platform solely for personal, non-commercial peer-to-peer lending and borrowing of physical items, in accordance with these Terms and all applicable laws.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">4.2 Prohibited Items</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            The following items may not be listed or transacted on the Platform under any circumstances:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Firearms, ammunition, and weapons of any kind</li>
            <li>Controlled substances, pharmaceuticals, or illegal drugs</li>
            <li>Live animals</li>
            <li>Motor vehicles (unless expressly permitted in a future Platform update)</li>
            <li>Items that are stolen, counterfeit, or subject to legal encumbrance</li>
            <li>Any item whose lending, borrowing, or transport is prohibited by applicable federal, state, or local law</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mt-3">
            Paradise Borrowed reserves the right to remove any listing and suspend or terminate any account associated with prohibited items, with or without prior notice.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">4.3 Prohibited Conduct</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            You agree not to:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Provide false or misleading information in any listing, profile, review, or dispute</li>
            <li>Circumvent the Platform's payment systems by transacting directly with other Users outside the Platform</li>
            <li>Harass, threaten, or harm other Users</li>
            <li>Use the Platform for any unlawful purpose</li>
            <li>Attempt to access, tamper with, or disrupt the Platform's systems or data</li>
            <li>Create multiple accounts to evade a suspension or ban</li>
            <li>Use automated tools, bots, or scrapers on the Platform without written authorization</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">5. Lender Terms</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">5.1 Listing Accuracy</h3>
          <p className="text-stone-600 leading-relaxed">
            Lenders are solely responsible for the accuracy, completeness, and legality of their listings. Listings must accurately describe the item's condition, functionality, and any known defects. Item condition must be disclosed using the Platform's condition rating scale at the time of listing creation.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">5.2 Insurance Responsibility</h3>
          <p className="text-stone-600 leading-relaxed">
            Lenders are solely responsible for maintaining appropriate personal property or renters insurance coverage for any items they list on the Platform. By listing an item, Lenders represent and warrant that they have the right to lend the item and that the item is covered under applicable personal property insurance or that the Lender accepts full risk of loss in the absence of coverage. Paradise Borrowed does not provide, arrange, or verify insurance coverage on behalf of any User.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">5.3 Item Condition at Handoff</h3>
          <p className="text-stone-600 leading-relaxed">
            Lenders are encouraged to photograph items before and after each booking. These records may be used as evidence in any dispute resolution process. Lenders who knowingly misrepresent item condition at the time of listing or handoff may be subject to account suspension or permanent termination.
          </p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">6. Borrower Terms</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">6.1 Care of Items</h3>
          <p className="text-stone-600 leading-relaxed">
            Borrowers agree to use all borrowed items with reasonable care, in accordance with their intended purpose, and in compliance with all applicable laws. Borrowers are responsible for any damage, loss, or destruction of borrowed items that occurs during the borrowing period, excluding normal wear and tear.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">6.2 Return Obligations</h3>
          <p className="text-stone-600 leading-relaxed">
            Borrowers must return items in the same condition as received by the agreed return date and time. Failure to return an item on time may result in additional charges and account suspension. Failure to return an item at all may be treated as theft and reported to law enforcement.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">6.3 Damage Responsibility</h3>
          <p className="text-stone-600 leading-relaxed">
            Borrowers are responsible for any damage, loss, or destruction of borrowed items that occurs during the borrowing period, excluding normal wear and tear. If damage occurs, the lender may file a dispute within 48 hours of return confirmation through the Platform's dispute process.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">7. Payments & Fees</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">7.1 Payment Processing</h3>
          <p className="text-stone-600 leading-relaxed">
            All payments on the Platform are processed through Stripe. By completing a transaction, you agree to Stripe's applicable terms of service and privacy policies. Paradise Borrowed does not store payment card numbers or sensitive financial data on its servers.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">7.2 Platform Fees</h3>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed charges fees on each completed transaction. The lender fee is deducted from the lender's payout; the borrower service fee is collected from the borrower at checkout and is subject to a minimum amount. Current fee rates are displayed at the time of booking and may be updated from time to time. Continued use of the Platform following a fee change constitutes acceptance of the revised fee structure.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-6 mb-2" data-testid="tos-promo-section">7.3 Promotional Platform Fee Waiver</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            Paradise Borrowed offers a location-based promotional program for early lenders:
          </p>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">ELIGIBILITY</h4>
          <p className="text-stone-600 leading-relaxed mb-3">
            The first ten (10) users per geographic market who publish a listing on Paradise Borrowed are eligible for promotional platform fee waiver. Geographic markets are defined as:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-3">
            <li>United States: City level (e.g., Miami, FL is separate from Fort Lauderdale, FL)</li>
            <li>Caribbean: Island level (e.g., Grand Cayman is separate from Nassau, Bahamas)</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mb-3">
            Eligibility is determined by the timestamp of first listing publication, not account creation. Users who sign up but never publish a listing do not claim a promotional slot.
          </p>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">PROMOTIONAL BENEFIT</h4>
          <p className="text-stone-600 leading-relaxed mb-3">
            Eligible users receive permanent platform fee waiver on their first ten (10) published listings. When these listings are booked:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-3">
            <li>Lender fee: 0% (the lender's 7% payout deduction is waived; the borrower service fee is unaffected and continues to apply)</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mb-3">
            Starting with an eligible user's eleventh (11th) published listing and all subsequent listings:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-3">
            <li>Lender fee: 7% (standard rate applies)</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mb-3">
            Ineligible users (those who publish after the first 10 slots in their location are filled):
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-3">
            <li>Lender fee: 7% on all listings</li>
          </ul>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">LOCATION DETERMINATION</h4>
          <p className="text-stone-600 leading-relaxed mb-3">
            Promotional eligibility is tied to the user's self-reported lending location (city or island). Users may only lend from one location at a time. Location changes require contacting Paradise Borrowed support and do not affect promotional eligibility status already granted.
          </p>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">LISTING SEQUENCE</h4>
          <p className="text-stone-600 leading-relaxed mb-3">
            Listing sequence numbers are assigned at initial publication and are permanent. Deleting or unpublishing a listing does not reclaim its sequence position.
          </p>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">REVOCATION</h4>
          <p className="text-stone-600 leading-relaxed mb-3">
            Paradise Borrowed reserves the right to revoke promotional eligibility if the user:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2 mb-3">
            <li>Violates these Terms of Service</li>
            <li>Is subject to permanent account suspension under &sect; 8.3</li>
            <li>Engages in fraudulent activity</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mb-3">
            Revocation is not automatic for account inactivity. Revoked users forfeit fee waiver status on all listings, including previously waived listings for future bookings. Completed bookings prior to revocation are not subject to retroactive fee charges.
          </p>

          <h4 className="font-semibold text-stone-800 mt-4 mb-1">NON-TRANSFERABLE</h4>
          <p className="text-stone-600 leading-relaxed">
            Promotional eligibility is tied to the user account and cannot be transferred, sold, or assigned to another user or account.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">7.3 Cancellations & Refunds</h3>
          <p className="text-stone-600 leading-relaxed">
            Cancellation terms are governed by the cancellation policy tier selected by the Lender at the time of listing creation and disclosed to the Borrower at booking. Paradise Borrowed is not responsible for issuing refunds outside the terms of the applicable cancellation policy.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">7.4 Taxes</h3>
          <p className="text-stone-600 leading-relaxed">
            Users are solely responsible for determining and remitting any taxes applicable to their transactions, including but not limited to income tax, sales tax, and VAT. Paradise Borrowed does not provide tax advice and does not withhold or remit taxes on behalf of Users, except as required by law.
          </p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">8. Reviews</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            Following each confirmed and completed transaction, both Lenders and Borrowers will be prompted to leave a rating and written review. Reviews must be honest, accurate, and based on the User's genuine experience. Reviews may not be defamatory, harassing, or otherwise in violation of these Terms or applicable law.
          </p>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed does not allow Users to edit or remove their own reviews once submitted. Reviews may only be removed by Paradise Borrowed if they are found to violate our Community Guidelines. We reserve the right to moderate, flag, or remove reviews at our sole discretion.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">9. Dispute Resolution</h2>
          <p className="text-stone-600 leading-relaxed">
            All disputes between Users arising from Platform transactions are governed by the Paradise Borrowed Dispute Resolution Policy, which is incorporated herein by reference and available at <Link to="/trust" className="text-coral hover:underline">paradiseborrowed.com/trust</Link>. By using the Platform, you agree to be bound by that Policy, including its provisions regarding internal mediation, binding arbitration, the class action waiver, and governing law.
          </p>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">10. Intellectual Property</h2>
          <p className="text-stone-600 leading-relaxed">
            All content, trademarks, logos, software, and proprietary technology on the Platform are the exclusive property of Paradise Borrowed, Inc. or its licensors. You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for its intended purpose. You may not copy, reproduce, distribute, modify, or create derivative works of any Platform content without our express written consent.
          </p>
        </section>

        {/* Section 11 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">11. Privacy</h2>
          <p className="text-stone-600 leading-relaxed">
            Your use of the Platform is also governed by our Privacy Policy, available at <Link to="/privacy-policy" className="text-coral hover:underline">paradiseborrowed.com/privacy</Link>, which is incorporated herein by reference. By using the Platform, you consent to the collection and use of your information as described in the Privacy Policy.
          </p>
        </section>

        {/* Section 12 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">12. Geographic Restrictions</h2>
          <p className="text-stone-600 leading-relaxed">
            The Platform is currently available only to Users located in or visiting supported regions, which include select states within the United States and select Caribbean territories. A complete list of supported locations is available on our website. Users in unsupported regions may not register for or use the Platform. We make no representation that the Platform complies with the laws of any jurisdiction outside our supported regions.
          </p>
        </section>

        {/* Section 13 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">13. Disclaimers</h2>
          <p className="text-stone-600 leading-relaxed uppercase text-sm mb-3">
            THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. PARADISE BORROWED DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
          <p className="text-stone-600 leading-relaxed uppercase text-sm">
            PARADISE BORROWED DOES NOT VET, ENDORSE, OR GUARANTEE ANY USER, LISTING, ITEM, OR TRANSACTION. YOUR USE OF THE PLATFORM AND ANY TRANSACTIONS YOU ENTER INTO ARE ENTIRELY AT YOUR OWN RISK.
          </p>
        </section>

        {/* Section 14 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">14. Limitation of Liability</h2>
          <p className="text-stone-600 leading-relaxed uppercase text-sm mb-3">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PARADISE BORROWED AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, LOSS OF DATA, PROPERTY DAMAGE, OR PERSONAL INJURY, ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM OR ANY TRANSACTION CONDUCTED THROUGH IT.
          </p>
          <p className="text-stone-600 leading-relaxed uppercase text-sm">
            IN NO EVENT SHALL PARADISE BORROWED'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE PLATFORM EXCEED THE GREATER OF (A) THE TOTAL FEES PAID BY YOU TO PARADISE BORROWED IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100).
          </p>
        </section>

        {/* Section 15 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">15. Indemnification</h2>
          <p className="text-stone-600 leading-relaxed">
            You agree to indemnify, defend, and hold harmless Paradise Borrowed, Inc. and its officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, judgments, awards, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to: (a) your use of the Platform; (b) your violation of these Terms; (c) your violation of any third-party rights, including those of other Users; or (d) any transaction you enter into through the Platform.
          </p>
        </section>

        {/* Section 16 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">16. Termination</h2>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed reserves the right to suspend or permanently terminate your account at any time, with or without notice, for any reason, including but not limited to violation of these Terms, fraudulent conduct, or behavior that harms other Users or the Platform. You may terminate your account at any time by contacting us at <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a>. Termination does not relieve you of obligations incurred prior to termination.
          </p>
        </section>

        {/* Section 17 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">17. Modifications to These Terms</h2>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed reserves the right to modify these Terms at any time. Material changes will be communicated to registered Users via email or in-app notification at least 14 days before taking effect. Your continued use of the Platform after the effective date of any modification constitutes your acceptance of the revised Terms.
          </p>
        </section>

        {/* Section 18 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">18. Governing Law & Venue</h2>
          <p className="text-stone-600 leading-relaxed">
            These Terms are governed by the laws of the State of Delaware, without regard to its conflict of law provisions. Any legal action not subject to arbitration under the Dispute Resolution Policy shall be brought exclusively in the state or federal courts located in Wilmington, Delaware, and you consent to the personal jurisdiction of such courts.
          </p>
        </section>

        {/* Section 19 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">19. Miscellaneous</h2>
          <ul className="list-none text-stone-600 space-y-3">
            <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions will continue in full force and effect.</li>
            <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and Dispute Resolution Policy, constitute the entire agreement between you and Paradise Borrowed regarding the Platform.</li>
            <li><strong>Waiver:</strong> Failure to enforce any provision of these Terms does not constitute a waiver of our right to enforce it in the future.</li>
            <li><strong>Assignment:</strong> You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights freely.</li>
          </ul>
        </section>

        {/* Section 20 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">20. Contact</h2>
          <div className="text-stone-600 leading-relaxed">
            <p className="font-semibold">Paradise Borrowed, Inc.</p>
            <p>Email: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a></p>
            <p>Website: <a href="https://www.paradiseborrowed.com" className="text-coral hover:underline">www.paradiseborrowed.com</a></p>
          </div>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-stone-200">
        <Link to="/privacy-policy" className="text-coral hover:underline">
          View Privacy Policy →
        </Link>
      </div>
      </div>
    </>
  );
}
